import React, {Fragment, useEffect, useState} from 'react';
import {
    Backdrop,
    Box,
    CircularProgress,
    Container,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    Grid,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import useStyles from '../styles/styles';
import theme from '../styles/theme';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import {Alert, Autocomplete} from '@material-ui/lab';
import {useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import _ from 'lodash';

const useBlockSourcesStyles = makeStyles({
    root: {
        color: '#000 !important',
        backgroundColor: '#fff !important',

        '& ul': {
            paddingLeft: theme.spacing(2),
            margin: 0,
        },
        '&.MuiExpansionPanel-root': {
            marginBottom: 0,
            '&.Mui-expanded': {
                margin: 0,
            }
        },
        '& .MuiExpansionPanelSummary-root': {
            padding: theme.spacing(0, 2, 0, 1),

            [theme.breakpoints.up('lg')]: {
                padding: theme.spacing(0, 2, 0, 0),
            },
            '&.Mui-disabled': {
                opacity: 1,
                backgroundColor: '#f6f6f6',
            },
        },
        '& .MuiExpansionPanelDetails-root': {
            padding: theme.spacing(0, 6, 0, 2),

            [theme.breakpoints.up('lg')]: {
                padding: theme.spacing(0, 6, 0, 0),
            },
        },
        '& .MuiAlert-root': {
            backgroundColor: 'transparent',
            color: 'inherit',
            padding: theme.spacing(0, 1, 0, 0),
        },
    },
    removeButton: {
        cursor: 'pointer',
        pointerEvents: 'all'
    },
    borderRight: {
        borderRight: '1px solid rgba(224, 224, 224, 1)'
    },
    table: {
        minWidth: '100%',
        margin: theme.spacing(0, 0, 5, 0),

        '& .MuiTableCell-root': {
            padding: theme.spacing(1),
            verticalAlign: 'top',
            width: '50%',
            whiteSpace: 'normal',
        },
        '& thead th': {
            height: 'auto',
            minHeight: 'auto',
        },
    },
    overview: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
        textAlign: 'left',
        height: '100%',
        padding: theme.spacing(2, 0),

        '& .MuiGrid-item': {
            borderLeft: '1px solid rgba(0, 0, 0, 0.5)',
            '&:first-of-type': {
                borderLeftColor: 'transparent',
            },
        },
        '& .MuiTypography-root': {
            fontSize: '4.5em',
            fontWeight: 900,
            lineHeight: 1,

            '& + .MuiTypography-root': {
                fontSize: '1.25em',
                fontWeight: 400,
            },
        },
        '& ul': {
            height: '100%',
            listStyleType: 'none',
            lineHeight: '1.4',
            margin: 0,
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
    },
    overviewHighlight: {
        backgroundColor: theme.palette.secondary.main,
        color: '#FFF',

        '& .MuiGrid-item': {
            borderLeftColor: 'rgba(255, 255, 255, 0.5)',
            '&:first-of-type': {
                borderLeftColor: 'transparent',
            },
        },
        '& ul': {
            fontWeight: 600,
        },
    }
});

const BlockedSources = () => {
    const classes = useStyles();
    const blockSourceClass = useBlockSourcesStyles();
    const [affiliatesArr, setAffiliatesArr] = useState(false);
    const [blockedSources, setBlockedSources] = useState(false);
    const [currentSources, setCurrentSources] = useState([]);
    const [expandedPanelsArr, setExpandedPanelsArr] = useState([]);
    const [manualLoad, setManualLoad] = useState(false);
    const {loading, error, data, networkStatus} = useQuery(gql`
        query BlockedSourcesPage {
            allCampaign {
                nodes {
                    id
                    name
                    portfolio {
                        id
                        name
                    }
                    servicer {
                        id
                        name
                    }
                    filterAttributes {
                        id
                        attributeType
                        description
                        type
                    }
                }
            }
            allAffiliate {
                nodes {
                    id
                    name
                    key
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                    minimumPrice
                    affiliate {
                        id
                        key
                    }
                }
            }
        }
    `, {
        notifyOnNetworkStatusChange: true,
    });

    /**
     * Loop Through All Affiliates and linked them to their traffic sources
     */
    useEffect(() => {
        if (data && Object.keys(data).length) {
            let campaignsBlocked = [];
            let sources;
            let trafficSourceArr;
            let affiliateSourceArr = [];
            setManualLoad(true);
            //Link Affiliates with Traffic Sources
            data.allAffiliate.nodes.forEach(affiliate => {
                trafficSourceArr = [];
                trafficSourceArr = data.allTrafficSource.nodes.filter(item => item.affiliate.key === affiliate.key);
                affiliateSourceArr.push({
                    affiliate,
                    allTrafficSourceArr: trafficSourceArr
                });
            });
            /**
             * Get the Blocked Affiliates/Traffic Sources from Campaign Filters
             * Create array of objects with campaign id, servicer and blocked sources
             */
            let blockedTrafficSource;
            let blockedAffiliateSource;
            data.allCampaign.nodes.forEach(campaign => {
                sources = [];
                const {id, filterAttributes, servicer} = campaign;
                sources = filterAttributes.filter(item => item.attributeType === "LEAD_SOURCE" && item.type === 'EXCLUDE');
                blockedTrafficSource = [];
                blockedAffiliateSource = [];
                if (sources.length) {
                    sources.forEach(source => {
                        const sourceVal = source.description.split('=');
                        if (sourceVal[0] === 'trafficSourceUUID') {
                            let ts = data.allTrafficSource.nodes.filter(ts => ts.id === sourceVal[1])[0];
                            if (ts) {
                                blockedTrafficSource.push(ts.id);
                            }
                        }
                        if (sourceVal[0] === 'affiliateKey' || sourceVal[0] === 'affiliateUUID') {
                            const key = sourceVal[0] === 'affiliateUUID' ? 'id' : 'key';
                            let affiliates = data.allAffiliate.nodes.filter(af => af[key] === sourceVal[1])[0];
                            if (affiliates) {
                                blockedAffiliateSource.push(affiliates.key);
                            }
                        }
                    })
                }
                campaignsBlocked.push({
                    id,
                    servicer,
                    blockedSources: {
                        affiliateSource: blockedAffiliateSource || [],
                        trafficSource: blockedTrafficSource || []
                    }
                });
            });
            /**
             * Link the Blocked Sources to the Affilites and Traffic Sources
             */
            affiliateSourceArr.forEach(obj => {
                const {affiliate, allTrafficSourceArr} = obj;
                obj.hasBlockedAffiliates = [];
                obj.hasBlockedTrafficSources = [];
                campaignsBlocked.forEach(campaign => {
                    const {id, blockedSources, servicer} = campaign;
                    let isAffiliateBlocked = blockedSources.affiliateSource.filter(a => a === affiliate.key);
                    if (isAffiliateBlocked.length) {
                        const isExistingServicer = obj.hasBlockedAffiliates.filter(item => item.servicerName === servicer.name);
                        if (isExistingServicer.length) {
                            const currentIndex = obj.hasBlockedAffiliates.indexOf(isExistingServicer[0]);
                            let existingItem = obj.hasBlockedAffiliates[currentIndex];
                            const isSameCampaignId = existingItem.campaignIds.filter(campaign => campaign === id);

                            if (!isSameCampaignId.length) {
                                existingItem.campaignIds = existingItem.campaignIds.concat(id);
                            }

                        } else {
                            obj.hasBlockedAffiliates.push({
                                servicerName: servicer.name,
                                campaignIds: [id],
                            });
                        }
                    }
                    allTrafficSourceArr.forEach(tsObj => {
                        let isTrafficSourceBlocked = blockedSources.trafficSource.filter(t => t === tsObj.id);
                        if (isTrafficSourceBlocked.length && affiliate.key === tsObj.affiliate.key) {
                            const isExistingTrafficSource = obj.hasBlockedTrafficSources.filter(item => item.servicerName === servicer.name);
                            if (isExistingTrafficSource.length) {
                                const currentTSIndex = obj.hasBlockedTrafficSources.indexOf(isExistingTrafficSource[0]);
                                const existingTS = obj.hasBlockedTrafficSources[currentTSIndex];
                                const isSameCampaignId = existingTS.campaignIds.filter(campaign => campaign === id);
                                const isSameTrafficSource = existingTS.arr.filter(ts => ts.id === tsObj.id);

                                if (!isSameCampaignId.length) {
                                    existingTS.campaignIds = existingTS.campaignIds.concat(id);
                                }

                                if (!isSameTrafficSource.length) {
                                    existingTS.arr = existingTS.arr.concat({
                                        id: tsObj.id,
                                        minimumPrice: tsObj.minimumPrice,
                                        name: tsObj.name
                                    });
                                }

                            } else {
                                obj.hasBlockedTrafficSources.push({
                                    servicerName: servicer.name,
                                    campaignIds: [id],
                                    arr: [{
                                        id: tsObj.id,
                                        minimumPrice: tsObj.minimumPrice,
                                        name: tsObj.name
                                    }]
                                });
                            }
                        }
                    });
                });

            });
            setAffiliatesArr(_.mapValues(_.groupBy(affiliateSourceArr, 'affiliate.key'), obj => obj[0]));
            setManualLoad(false);
        }
    }, [data]);

    useEffect(() => {
        if (currentSources.length) {
            let finalActiveBlocks = [];
            currentSources.forEach(source => {
                const match = affiliatesArr[source.key];
                if (match) {
                    finalActiveBlocks.push(match);
                }
            });

            setBlockedSources(finalActiveBlocks);
        } else {
            setBlockedSources(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSources]);


    if (loading || networkStatus === 4 || manualLoad) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        );
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
            </Paper>
        );
    }
    const servicersByCampaign = _.mapValues(_.groupBy(data.allCampaign.nodes, 'servicer.name'));
    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace}>
                Blocked Sources
            </Typography>
            <Paper className={classes.paper}>
                <Container maxWidth={false} className={classes.root}>
                    <Grid container spacing={2} justify="center" alignItems="center">
                        <Grid item xs={12}>
                            {data && data.allAffiliate.nodes ? (
                                <Autocomplete
                                    multiple
                                    id="tags-outlined"
                                    value={currentSources}
                                    options={data.allAffiliate.nodes}
                                    getOptionLabel={(option) => option.name}
                                    filterSelectedOptions
                                    onChange={(e, val) => {
                                        setCurrentSources(val);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Affiliates"
                                            placeholder="Affiliates"
                                        />
                                    )}
                                />
                            ) : null}
                        </Grid>
                        <Grid item xs={12}>
                            {blockedSources && blockedSources.length ? (
                                <>
                                    {blockedSources.map(item => {
                                        const {affiliate, hasBlockedAffiliates, hasBlockedTrafficSources} = item;
                                        const {name, key} = affiliate;
                                        const isDisabled = !hasBlockedAffiliates.length && !hasBlockedTrafficSources.length;
                                        const isExpanded = expandedPanelsArr.length ? expandedPanelsArr.includes(key) : false;
                                        return (
                                            <ExpansionPanel
                                                expanded={isExpanded}
                                                className={blockSourceClass.root}
                                                key={name}
                                                disabled={isDisabled}
                                                onChange={(e, bool) => {
                                                    if (bool) {
                                                        setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, key]);
                                                    } else {
                                                        if (expandedPanelsArr.includes(key)) {
                                                            setExpandedPanelsArr(expandedPanelsArr.filter(panel => panel !== key));
                                                        }
                                                    }
                                                }}
                                            >
                                                <ExpansionPanelSummary
                                                    expandIcon={!isDisabled ? <ExpandMoreIcon/> : null}
                                                    aria-controls="panel1c-content"
                                                    id="panel1c-header"
                                                >
                                                    <Grid container>
                                                        <Grid item xs={12} md={3}>
                                                            <Box display="flex" flexDirection="row" alignItems="center">
                                                                <Box>
                                                                    <IconButton
                                                                        className={blockSourceClass.removeButton}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setCurrentSources(currentSources.filter(item => item.name !== name));
                                                                        }}>
                                                                        <RemoveCircleIcon/>
                                                                    </IconButton>
                                                                </Box>
                                                                <Box textAlign="left">
                                                                    <Typography variant="h6">{name}</Typography>
                                                                    {!isDisabled ? (
                                                                        <Typography variant="caption">
                                                                            {isExpanded ? 'Close Details' : 'View Details'}
                                                                        </Typography>
                                                                    ) : null}
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                        {isDisabled ? (
                                                            <Grid item xs={12} md={8}>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    height="100%"
                                                                >
                                                                    <Alert severity="success">This affiliate currently
                                                                        has no blocks!</Alert>
                                                                </Box>
                                                            </Grid>
                                                        ) : (
                                                            <Grid container item xs={12} md={9} spacing={2}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Box
                                                                        className={`${blockSourceClass.overview} ${hasBlockedAffiliates.length && blockSourceClass.overviewHighlight}`}
                                                                    >
                                                                        <Grid container>
                                                                            <Grid item xs={4}>
                                                                                <Box textAlign="center">
                                                                                    <Typography variant="subtitle1">
                                                                                        {hasBlockedAffiliates.length}
                                                                                    </Typography>
                                                                                    <Typography variant="subtitle1">
                                                                                        Servicers
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={8}>
                                                                                <Box
                                                                                    component="ul"
                                                                                >
                                                                                    {!hasBlockedAffiliates.length ? (
                                                                                        <Box
                                                                                            component="li"
                                                                                        >
                                                                                            <Alert severity="success">
                                                                                                There are no servicer
                                                                                                level blocks
                                                                                            </Alert>
                                                                                        </Box>
                                                                                    ) : null}
                                                                                    {hasBlockedAffiliates.map((servicer, idx) => (
                                                                                        <Box
                                                                                            component="li"
                                                                                            key={servicer.servicerName}
                                                                                        >
                                                                                            {servicer.servicerName || 'N/A'}
                                                                                        </Box>
                                                                                    ))}
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <Box
                                                                        className={`${blockSourceClass.overview} ${hasBlockedTrafficSources.length && blockSourceClass.overviewHighlight}`}
                                                                    >
                                                                        <Grid container>
                                                                            <Grid item xs={4}>
                                                                                <Box textAlign="center">
                                                                                    <Typography variant="subtitle1">
                                                                                        {hasBlockedTrafficSources.length}
                                                                                    </Typography>
                                                                                    <Typography variant="subtitle1">
                                                                                        Traffic Sources
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={8}>
                                                                                <Box
                                                                                    component="ul"
                                                                                >
                                                                                    {!hasBlockedTrafficSources.length ? (

                                                                                        <Box
                                                                                            component="li"
                                                                                        >
                                                                                            <Alert severity="success">
                                                                                                There are no traffic
                                                                                                source level blocks
                                                                                            </Alert>
                                                                                        </Box>
                                                                                    ) : null}
                                                                                    {hasBlockedTrafficSources.map((servicer, idx) => (
                                                                                        <Box
                                                                                            component="li"
                                                                                            key={servicer.servicerName}
                                                                                        >
                                                                                            {servicer.servicerName}
                                                                                        </Box>
                                                                                    ))}
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </ExpansionPanelSummary>
                                                <ExpansionPanelDetails className={classes.details}>
                                                    <Grid container>
                                                        <Grid item lg={3}/>
                                                        <Grid item container xs lg={9} spacing={2}>
                                                            <Grid item xs={12} lg={6}>
                                                                <TableContainer>
                                                                    {hasBlockedAffiliates.length ? (
                                                                        <Table
                                                                            className={`${classes.table} ${blockSourceClass.table}`}
                                                                            size="small"
                                                                            padding="none"
                                                                            aria-label="spanning table"
                                                                        >
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell
                                                                                        align="left">Servicer</TableCell>
                                                                                    <TableCell align="left">Campaign
                                                                                        Ids</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {hasBlockedAffiliates.map((servicer, idx) => (
                                                                                    <TableRow
                                                                                        key={servicer.servicerName}>
                                                                                        <TableCell>{servicer.servicerName || 'N/A'}&nbsp;{`(${servicer.campaignIds.length}/ ${servicersByCampaign[servicer.servicerName].length})`}</TableCell>
                                                                                        <TableCell>
                                                                                            <Box component="ul"
                                                                                                 margin={0}>
                                                                                                {servicer.campaignIds.map((campaignIds, idx) => (
                                                                                                    <Box component="li"
                                                                                                         key={campaignIds}>{campaignIds}</Box>
                                                                                                ))}
                                                                                            </Box>
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    ) : null}
                                                                </TableContainer>
                                                            </Grid>
                                                            <Grid item xs={12} lg={6}>
                                                                <TableContainer>
                                                                    {hasBlockedTrafficSources.length ? (
                                                                        <Table
                                                                            className={`${classes.table} ${blockSourceClass.table}`}
                                                                            size="small"
                                                                            padding="none"
                                                                            aria-label="spanning table"
                                                                        >
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell align="left">Traffic
                                                                                        Source/Servicer</TableCell>
                                                                                    <TableCell align="left">Campaign
                                                                                        Ids</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                <>
                                                                                    {hasBlockedTrafficSources.map((servicer, idx) => (
                                                                                        <Fragment key={idx.toString()}>
                                                                                            {servicer.arr.map(trafficSource => (
                                                                                                <TableRow
                                                                                                    key={trafficSource.id}>
                                                                                                    <TableCell>
                                                                                                        <strong>
                                                                                                            {trafficSource.name} | {trafficSource.minimumPrice}
                                                                                                        </strong>

                                                                                                        <br/>
                                                                                                        {servicer.servicerName || 'N/A'}&nbsp;{`(${servicer.campaignIds.length}/ ${servicersByCampaign[servicer.servicerName].length})`}
                                                                                                    </TableCell>
                                                                                                    <TableCell>
                                                                                                        <Box
                                                                                                            component="ul"
                                                                                                            margin={0}>
                                                                                                            {servicer.campaignIds.map((campaignIds, idx) => (
                                                                                                                <Box
                                                                                                                    component="li"
                                                                                                                    key={campaignIds}>
                                                                                                                    {campaignIds}
                                                                                                                </Box>
                                                                                                            ))}
                                                                                                        </Box>
                                                                                                    </TableCell>
                                                                                                </TableRow>
                                                                                            ))}
                                                                                        </Fragment>
                                                                                    ))}
                                                                                </>
                                                                            </TableBody>
                                                                        </Table>
                                                                    ) : null}
                                                                </TableContainer>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </ExpansionPanelDetails>
                                            </ExpansionPanel>
                                        );
                                    })}
                                </>
                            ) : (
                                <Alert severity="warning">Please select an affiliate to see the affiliates block
                                    sources.</Alert>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </Paper>
        </>
    );
};

export default BlockedSources;
